/**
 * Polynômes de Taylor en 0 — coefficients EXACTS (fractions) des
 * développements limités usuels, évaluation et zone de bonne approximation.
 */

export interface Frac {
  p: number;
  q: number;
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

export function frac(p: number, q = 1): Frac {
  if (q < 0) {
    p = -p;
    q = -q;
  }
  const g = gcd(p, q);
  return { p: p / g, q: q / g };
}

const ZERO = frac(0);

function factorial(k: number): number {
  let r = 1;
  for (let i = 2; i <= k; i++) r *= i;
  return r;
}

export const FUNCTION_IDS = ['exp', 'sin', 'cos', 'ln1p', 'geom', 'sqrt1p'] as const;
export type FunctionId = (typeof FUNCTION_IDS)[number];

export interface TaylorFunction {
  id: FunctionId;
  /** bouton */
  label: string;
  /** f(x) en KaTeX */
  tex: string;
  f: (x: number) => number;
  /** coefficient exact de xᵏ */
  coef: (k: number) => Frac;
}

/** coefficients de √(1 + x) : c₀ = 1, cₖ = cₖ₋₁ × (3 − 2k) / (2k) */
const SQRT_COEFS: Frac[] = (() => {
  const out: Frac[] = [frac(1)];
  for (let k = 1; k <= 12; k++) {
    const prev = out[k - 1]!;
    out.push(frac(prev.p * (3 - 2 * k), prev.q * 2 * k));
  }
  return out;
})();

export const FUNCTIONS: readonly TaylorFunction[] = [
  {
    id: 'exp',
    label: 'eˣ',
    tex: 'e^x',
    f: Math.exp,
    coef: (k) => frac(1, factorial(k)),
  },
  {
    id: 'sin',
    label: 'sin x',
    tex: '\\sin x',
    f: Math.sin,
    coef: (k) => (k % 2 === 1 ? frac((k - 1) % 4 === 0 ? 1 : -1, factorial(k)) : ZERO),
  },
  {
    id: 'cos',
    label: 'cos x',
    tex: '\\cos x',
    f: Math.cos,
    coef: (k) => (k % 2 === 0 ? frac(k % 4 === 0 ? 1 : -1, factorial(k)) : ZERO),
  },
  {
    id: 'ln1p',
    label: 'ln(1 + x)',
    tex: '\\ln(1+x)',
    f: (x) => (x > -1 ? Math.log1p(x) : NaN),
    coef: (k) => (k === 0 ? ZERO : frac(k % 2 === 1 ? 1 : -1, k)),
  },
  {
    id: 'geom',
    label: '1/(1 − x)',
    tex: '\\dfrac{1}{1-x}',
    f: (x) => (x !== 1 ? 1 / (1 - x) : NaN),
    coef: () => frac(1),
  },
  {
    id: 'sqrt1p',
    label: '√(1 + x)',
    tex: '\\sqrt{1+x}',
    f: (x) => (x >= -1 ? Math.sqrt(1 + x) : NaN),
    coef: (k) => SQRT_COEFS[k] ?? ZERO,
  },
];

export function functionById(id: FunctionId): TaylorFunction {
  return FUNCTIONS.find((f) => f.id === id)!;
}

export const ORDER_MAX = 10;

/** Tₙ(x) = Σ cₖ xᵏ (Horner) */
export function taylorValue(fn: TaylorFunction, n: number, x: number): number {
  let r = 0;
  for (let k = n; k >= 0; k--) {
    const c = fn.coef(k);
    r = r * x + c.p / c.q;
  }
  return r;
}

/** Tₙ en KaTeX : « 1 + x + \frac{x^{2}}{2} − … », 0 si tous les termes sont nuls. */
export function taylorTex(fn: TaylorFunction, n: number): string {
  const parts: string[] = [];
  for (let k = 0; k <= n; k++) {
    const c = fn.coef(k);
    if (c.p === 0) continue;
    const neg = c.p < 0;
    const a = Math.abs(c.p);
    const xk = k === 0 ? '' : k === 1 ? 'x' : `x^{${k}}`;
    let term: string;
    if (k === 0) term = c.q === 1 ? `${a}` : `\\frac{${a}}{${c.q}}`;
    else if (c.q === 1) term = a === 1 ? xk : `${a}${xk}`;
    else term = `\\frac{${a === 1 ? '' : a}${xk}}{${c.q}}`;
    if (parts.length === 0) parts.push(neg ? `-${term}` : term);
    else parts.push(`${neg ? '-' : '+'} ${term}`);
  }
  return parts.length ? parts.join(' ') : '0';
}

/**
 * Autour de 0, le plus grand intervalle [α ; β] de la fenêtre où
 * |f − Tₙ| < tol (échantillonné). null si même en 0 l'écart dépasse tol.
 */
export function goodZone(
  fn: TaylorFunction,
  n: number,
  range: [number, number],
  tol = 0.1,
  samples = 800,
): [number, number] | null {
  const ok = (x: number) => {
    const d = Math.abs(fn.f(x) - taylorValue(fn, n, x));
    return Number.isFinite(d) && d < tol;
  };
  if (!ok(0)) return null;
  const step = (range[1] - range[0]) / samples;
  let lo = 0;
  while (lo - step >= range[0] && ok(lo - step)) lo -= step;
  let hi = 0;
  while (hi + step <= range[1] && ok(hi + step)) hi += step;
  return [lo, hi];
}
